import { Check, ArrowRight } from "lucide-react"
import { STAGE_ORDER, STAGE_META, stageIndex } from "./theme"

// ── ProcessStepper ─────────────────────────────────────────────────────────
// Horizontal workflow stepper for the Order Request form and other procurement
// detail pages (e.g. Draft → Info Requested → Waiting for Approval →
// Approved → Closed — the exact `steps` list is caller-defined). Completed
// steps get a filled node + check icon, the current step is ringed and tagged
// "Current", upcoming steps are muted; the connector leading into a step is
// filled once the record has reached it, so the line itself shows progress.
// Purely index-based: any step earlier than the current one renders as
// "done", even a conditional one (like Info Requested) the current record
// never actually passed through — callers should keep that in mind when
// choosing where to place a branch-only step in a linear `steps` list.
//
// steps: [{ key, label, meta? }]. currentKey = active status. `meta` is an
// optional line under the label (e.g. a timestamp) — callers only pass it
// when they actually have the data.
export const ProcessStepper = ({ steps, currentKey }) => {
  const currentIdx = steps.findIndex((s) => s.key === currentKey)
  return (
    <ol className="pc-steps" aria-label="Progress">
      {steps.map((step, idx) => {
        const done = currentIdx > idx && currentIdx !== -1
        const active = currentIdx === idx
        const state = done ? "done" : active ? "active" : "upcoming"
        return (
          <li
            className={`pc-step pc-step--${state}`}
            key={step.key}
            aria-current={active ? "step" : undefined}
          >
            {idx > 0 && (
              <span
                className={`pc-step-bar${currentIdx >= idx ? " pc-step-bar--done" : ""}`}
                aria-hidden
              />
            )}
            <span className="pc-step-dot">{done ? <Check aria-hidden /> : idx + 1}</span>
            <span className="pc-step-label">{step.label}</span>
            {active ? (
              <span className="pc-step-meta pc-step-meta--current">{step.meta || "Current"}</span>
            ) : (
              step.meta && <span className="pc-step-meta">{step.meta}</span>
            )}
          </li>
        )
      })}
    </ol>
  )
}

// ── StageStepper ───────────────────────────────────────────────────────────
// Compact P2P chain (Order → Purchase → GRN → Payment) for the My Orders list
// cell and the tracking summary. Dots up to the current stage are filled;
// the current stage is ringed. Shows the current stage label alongside.
export const StageStepper = ({ currentStage, showLabel = true }) => {
  const cur = stageIndex(currentStage)
  return (
    <span className="pc-stage" title={STAGE_META[currentStage]?.label}>
      {STAGE_ORDER.map((code, idx) => {
        const done = idx < cur
        const active = idx === cur
        return (
          <span className="pc-stage-node" key={code}>
            <span
              className={[
                "pc-stage-dot",
                done ? "pc-stage-dot--done" : "",
                active ? "pc-stage-dot--active" : "",
              ]
                .filter(Boolean)
                .join(" ")}
            >
              {done && <Check aria-hidden />}
            </span>
            {idx < STAGE_ORDER.length - 1 && (
              <span
                className={`pc-stage-connector${done ? " pc-stage-connector--done" : ""}`}
              />
            )}
          </span>
        )
      })}
      {showLabel && (
        <span className="pc-stage-current-label">
          {STAGE_META[currentStage]?.short || currentStage}
        </span>
      )}
    </span>
  )
}

// ── LifecycleTimeline ──────────────────────────────────────────────────────
// Vertical tracking timeline with meaningful connectors:
//   completed  → filled node + check + solid connector
//   current    → outlined ringed node + dashed connector
//   upcoming   → muted node + muted connector
// Each item may carry `sub` (e.g. payment batches) rendered as an inset list.
//
// items: [{ title, meta?, state: "done"|"current"|"upcoming", badge?, sub?: node }]
export const LifecycleTimeline = ({ items = [] }) => (
  <div className="pc-tl">
    {items.map((item, idx) => {
      const isLast = idx === items.length - 1
      const nodeMod =
        item.state === "done"
          ? "pc-tl-node--done"
          : item.state === "current"
          ? "pc-tl-node--current"
          : ""
      // Connector below a node reflects progress *out of* this node.
      const connMod =
        item.state === "done"
          ? "pc-tl-connector--done"
          : item.state === "current"
          ? "pc-tl-connector--dashed"
          : ""
      return (
        <div className={`pc-tl-item pc-tl-item--${item.state}`} key={idx}>
          <div className="pc-tl-rail">
            <span className={`pc-tl-node ${nodeMod}`}>
              {item.state === "done" ? <Check aria-hidden /> : idx + 1}
            </span>
            {!isLast && <span className={`pc-tl-connector ${connMod}`} />}
          </div>
          <div className="pc-tl-body">
            <div className="pc-tl-title">
              {item.title}
              {item.badge}
            </div>
            {item.meta && <div className="pc-tl-meta">{item.meta}</div>}
            {item.sub}
          </div>
        </div>
      )
    })}
  </div>
)

// ── ApprovalRoute ──────────────────────────────────────────────────────────
// Approver steps for the Order Request "Approval Route Preview".
// steps: [{ order, approverLabel, mode, modeLabel }]. Horizontal by default
// (Order Detail / Review); `vertical` renders it as a top-to-bottom flow with
// a connector between steps (Create Order).
export const ApprovalRoute = ({ steps = [], vertical = false }) =>
  vertical ? (
    <ol className="pc-route pc-route--vertical">
      {steps.map((s, idx) => (
        <li className="pc-route-vstep" key={s.order ?? idx}>
          <span className="pc-route-idx">{s.order ?? idx + 1}</span>
          <div className="pc-route-vbody">
            <div className="pc-route-name">{s.approverLabel}</div>
            <div className="pc-route-mode">{s.modeLabel || s.mode}</div>
          </div>
        </li>
      ))}
    </ol>
  ) : (
    <div className="pc-route">
      {steps.map((s, idx) => (
        <div className="pc-route-step" key={s.order ?? idx}>
          <div className="pc-route-card">
            <span className="pc-route-idx">{s.order ?? idx + 1}</span>
            <div style={{ minWidth: 0 }}>
              <div className="pc-route-name">{s.approverLabel}</div>
              <div className="pc-route-mode">{s.modeLabel || s.mode}</div>
            </div>
          </div>
          {idx < steps.length - 1 && (
            <span className="pc-route-arrow">
              <ArrowRight aria-hidden />
            </span>
          )}
        </div>
      ))}
    </div>
  )
